import { Line, LineConfig, PlotEvent } from '@ant-design/charts';
import { useLazyQuery, useQuery } from '@apollo/client';
import {
  GET_TOTAL_CHALLENGES,
  GET_TOTAL_CHALLENGES_COUNT,
  TotalChallenges,
  TotalChallengesData,
} from '@frontend/data-access';
import { Card, DatePicker } from 'antd';
import { RangePickerProps } from 'antd/es/date-picker';
import dayjs from 'dayjs';
import useTranslation from 'next-translate/useTranslation';
import { RefAttributes, useEffect, useMemo, useState } from 'react';

const { RangePicker } = DatePicker;

export enum ChallengeState {
  OPEN = 'OPEN',
  INTERNAL = 'INTERNAL',
}

const COLOR_PLATE_10 = [
  '#00CC00',
  '#2FC25B',
  '#2888ED',
  '#62BAFC',
  '#36CBCB',
  '#525355',
];

const TotalOpenChallengeStats = () => {
  const { t } = useTranslation('admin');
  const { t: tcommon } = useTranslation('common');
  const [data, setData] = useState<TotalChallengesData[]>([]);
  const endDate = dayjs();
  const startDate = endDate.subtract(3, 'month');
  const [dates, setDates] = useState([
    startDate.format('YYYY-MM-DD'),
    endDate.format('YYYY-MM-DD'),
  ]);

  const statusOptions = useMemo(
    () => [
      { label: t('registration_open'), value: 'ACTIVE' },
      { label: t('registration_closed'), value: 'ENDED' },
      { label: t('upcoming'), value: 'UPCOMING' },
      { label: t('draft'), value: 'DRAFT' },
      { label: t('archived'), value: 'ARCHIVED' },
    ],
    [t]
  );

  const monthNames = useMemo(
    () => [
      tcommon('Jan'),
      tcommon('Feb'),
      tcommon('Mar'),
      tcommon('Apr'),
      tcommon('May'),
      tcommon('Jun'),
      tcommon('Jul'),
      tcommon('Aug'),
      tcommon('Sep'),
      tcommon('Oct'),
      tcommon('Nov'),
      tcommon('Dec'),
    ],
    [tcommon]
  );

  const { data: dataTotal } = useQuery<{ totalChallengesCount: number }>(
    GET_TOTAL_CHALLENGES_COUNT,
    {
      variables: {
        type: ChallengeState.OPEN,
      },
    }
  );
  const [gatData, { loading }] = useLazyQuery<{
    challengeStatisticsByState: TotalChallenges[];
  }>(GET_TOTAL_CHALLENGES, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const sortedStatistics = data.challengeStatisticsByState.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );

      const aggregatedData: TotalChallengesData[] = [];
      const result: TotalChallengesData[] = [];

      // Add entries for each status and accumulate counts for each date
      for (const state of sortedStatistics) {
        let totalValue = 0;

        // Collect individual status data and accumulate for aggregation
        for (const status of statusOptions) {
          const value = state.values.find((v) => v.state === status.value);
          if (value) {
            result.push({
              name: status.label,
              date: state.date,
              value: value.count,
            });
            totalValue += value.count;
          }
        }

        // Add the aggregated "all" count entry for each date
        aggregatedData.push({
          name: 'All',
          date: state.date,
          value: totalValue,
        });
      }

      const finalResult = [...aggregatedData, ...result];
      setData(finalResult);
    },
  });

  useEffect(() => {
    if (!dates) return;
    gatData({
      variables: {
        input: {
          startDate: dates[0],
          endDate: dates[1],
          type: ChallengeState.OPEN,
        },
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dates]);

  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const defaultSelectedItems = useMemo(() => {
    if (data.length > 0) {
      return data.map((item) => item.name);
    }
    return [];
  }, [data]);

  useEffect(() => {
    setSelectedItems(defaultSelectedItems);
  }, [defaultSelectedItems]);

  const config = useMemo<LineConfig & RefAttributes<unknown>>(
    () => ({
      data: data,
      xField: 'date',
      yField: 'value',
      seriesField: 'name',
      colorField: 'name',
      loading: loading,
      animate: { enter: { type: 'pathIn' } },
      style: { lineWidth: 2 },
      axis: {
        x: {
          labelFormatter: (v) => {
            const monthIndex = parseInt(v.split('-')[1], 10) - 1;
            return monthNames[monthIndex];
          },
        },
      },
      legend: {
        color: {
          position: 'bottom',
          autoWrap: true,
          itemMarker: 'square',
          style: {
            fontSize: 14,
            fontFamily: 'Lufga',
          },
          layout: {
            justifyContent: 'center',
            alignItems: 'center',
          },
        },
      },
      onReady: ({ chart }) => {
        chart.on('legend:filter', (e: PlotEvent) => {
          if (e?.data?.values) {
            const values = e?.data?.values as Array<string>;
            setSelectedItems([...values]);
          }
        });
      },
      interactions: [
        {
          type: 'legend-filter',
        },
      ],
      animation: {
        appear: {
          animation: 'path-in',
          duration: 5000,
        },
      },
      label: {
        text: 'value',
        position: 'top',
        textAlign: 'center',
        dy: -10,
        style: {
          fontFamily: 'Lufga',
        },
      },
      scale: {
        color: {
          range: COLOR_PLATE_10,
        },
      },
    }),
    [data, loading, monthNames]
  );

  const visibleTotal = useMemo(() => {
    if (selectedItems.includes('All')) {
      return data
        .filter((item) => item.name === 'All')
        .reduce((acc, curr) => acc + curr.value, 0);
    } else {
      return data
        .filter(
          (item) => item.name !== 'All' && selectedItems.includes(item.name)
        )
        .reduce((acc, curr) => acc + curr.value, 0);
    }
  }, [data, selectedItems]);

  const handleChange: RangePickerProps['onChange'] = (dates, dateStrings) => {
    if (dates) {
      setDates([dateStrings[0], dateStrings[1]]);
    }
  };

  return (
    <Card
      style={{
        borderRadius: 20,
      }}
    >
      <div className="flex flex-row justify-between">
        <h2 className="flex items-center flex-wrap gap-1">
          <span>{t('total_during_period')}</span>
          <span className="text-gray-700 font-medium">
            / {t('total_all_time')}
          </span>
        </h2>
        <div>
          <RangePicker
            onChange={handleChange}
            defaultValue={[dayjs(dates[0]), dayjs(dates[1])]}
            style={{
              borderRadius: 20,
            }}
          />
        </div>
      </div>
      <div className="flex items-center flex-wrap gap-1 my-4">
        <span className="text-gray-900 text-3xl">{visibleTotal}</span>
        <span className="text-gray-700 text-2xl">
          / {dataTotal?.totalChallengesCount}
        </span>
      </div>
      <Line {...config} />
    </Card>
  );
};

export default TotalOpenChallengeStats;
